import localStore from './localStore'

// Where a pending invitation is kept between the landing link and the sign-in.
export const INVITATION_STORAGE_KEY = 'invitationToken'

// The site an invitation link names in the clear, beside the encrypted invitation.
// Sent to Keycloak as dhi_invited_site when signing in with an invitation pending,
// so its "finish setting up your account" page can say who invited the person,
// and where - the invitation this link belongs to, not another one.
export const INVITED_SITE = 'invitedSite'
export const INVITED_SITE_AUTH_PARAM = 'dhi_invited_site'

// The link's random key, also in the clear. Sent to Keycloak as
// dhi_invitation_key so its sign-in screen can show the invitation - "Anna
// invited you to Site A" - before the person has typed anything. Keycloak shows
// it only for a key whose hash it holds, i.e. only to somebody who has the email.
export const INVITATION_KEY = 'invitationKey'
export const INVITATION_KEY_AUTH_PARAM = 'dhi_invitation_key'

// When the key was taken from the link. The key is sent for an hour only: an
// invitee who opens the link and walks away from a shared computer must not leave
// "Anna invited you..." and their address on the next person's sign-in screen.
// The invitation itself is unaffected - it is accepted with the encrypted part.
export const INVITATION_KEY_SAVED_AT = 'invitationKeySavedAt'
export const INVITATION_KEY_TTL_MS = 60 * 60 * 1000

const pendingValue = (field: string): string | undefined => {
  const pending = localStore.get(INVITATION_STORAGE_KEY)
  const value =
    pending && typeof pending === 'object' ? pending[field] : undefined
  return typeof value === 'string' && value !== '' ? value : undefined
}

export const pendingInvitedSite = (): string | undefined =>
  pendingValue(INVITED_SITE)

export const pendingInvitationKey = (
  now: number = Date.now()
): string | undefined => {
  const pending = localStore.get(INVITATION_STORAGE_KEY)
  const savedAt =
    pending && typeof pending === 'object'
      ? pending[INVITATION_KEY_SAVED_AT]
      : undefined
  if (typeof savedAt !== 'number' || now - savedAt > INVITATION_KEY_TTL_MS)
    return undefined
  return pendingValue(INVITATION_KEY)
}

// The invitation's own parameters, which must not leave the browser for analytics.
const INVITATION_PARAMS = [
  'invitationVersion',
  'invitationIv',
  'invitation',
  INVITED_SITE,
  INVITATION_KEY
]

// The address without the invitation's parameters, as reported to Matomo. An
// invitation link may arrive with its whole query encoded once more
// (InvitationProvider.getQueryParams), so that form is decoded first.
export const withoutInvitationParams = (href: string): string => {
  const url = new URL(href)
  const raw = url.search.includes('invitationVersion%3D')
    ? decodeURIComponent(url.search)
    : url.search
  const params = new URLSearchParams(raw)
  if (!INVITATION_PARAMS.some(p => params.has(p))) return href
  INVITATION_PARAMS.forEach(p => params.delete(p))
  url.search = params.toString()
  return url.toString()
}

// With an invitation pending, adds its site and key to a sign-in's extra parameters.
export const withInvitedSite = (
  extras: Record<string, string> = {}
): Record<string, string> => {
  const site = pendingInvitedSite()
  const key = pendingInvitationKey()
  return {
    ...extras,
    ...(site ? { [INVITED_SITE_AUTH_PARAM]: site } : {}),
    ...(key ? { [INVITATION_KEY_AUTH_PARAM]: key } : {})
  }
}

// The extras of a sign-in the person takes part in: the same the landing page's
// silent attempt sends - no site chosen yet while an invitation is pending, and
// that invitation's site and key - so Keycloak's sign-in screen can show it.
export const interactiveSignInExtras = (): Record<string, string> =>
  withInvitedSite({
    noTenant: String(pendingValue('invitation') !== undefined)
  })

// The extras of the sign-in that follows accepting an invitation, so the new token
// is for the site just joined. tenantId is what the platform's old OAuth server
// read; Keycloak reads dhi_invited_site, and selects that site even when this
// browser is already signed in to another one.
export const signInToAcceptedSiteExtras = (
  tenantId: string
): Record<string, string> => ({
  tenantId,
  noTenant: 'false',
  [INVITED_SITE_AUTH_PARAM]: tenantId
})

// Whether the token in hand is already for the site an invitation was just
// accepted into. Keycloak accepts the invitation while signing the person in and
// puts that site in the token, so the sign-in after accepting would only fetch
// the same token again - and with nothing gained, every extra round trip is one
// more chance to land on the sign-in screen.
export const signedInToSite = (
  idTokenPayload: Record<string, unknown> | null | undefined,
  tenantId: string
): boolean => {
  const current = idTokenPayload?.TenantId ?? idTokenPayload?.tenant_id
  return (
    typeof current === 'string' &&
    current.toLowerCase() === tenantId.toLowerCase()
  )
}

// Where the landing page's silent sign-in would have returned to, kept for the
// interactive sign-in that replaces it when Keycloak has no session.
export const SIGN_IN_RETURN_PATH = 'signInReturnPath'

// What an authorization server answers a silent sign-in (prompt=none) that needs
// the person: no session, or one that needs them to choose or consent. Keycloak
// honours prompt=none and says so; the platform's old OAuth server ignored it and
// showed its login page, which is why this never surfaced before.
const NEEDS_THE_PERSON = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required'
]

export const silentSignInNeedsThePerson = (search: string): boolean =>
  NEEDS_THE_PERSON.includes(new URLSearchParams(search).get('error') ?? '')
